import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowsRightLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, EmptyState, Modal, RowMenu, Select, Table, Tag, Textarea } from "../../ui";
import { errorMessageOf, scholarshipsApi } from "../../../api/scholarships";
import ApplicationDialog from "../review/ApplicationDialog";
import { REVIEW_KEY, formatDateTime, formatScore, fullName } from "../review/model";
import { BENEFICIARY_STATUSES, beneficiariesKey, beneficiaryStatusOf, useBeneficiaries } from "./model";

const MAX_REASON = 1000;

const statusTag = (b) => <Tag color={beneficiaryStatusOf(b.status).color}>{beneficiaryStatusOf(b.status).label}</Tag>;

// Records one step for the chosen awardees, with an optional note
function StatusDialog({ scholarship, chosen, onClose, onDone }) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const unchanged = status ? chosen.filter((b) => b.status === status) : [];
  const names = (list) => list.map((b) => fullName(b.applicant)).join(", ");

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const { data } = await scholarshipsApi.post(`staff/scholarships/${scholarship.id}/beneficiaries/status`, {
        applicationIds: chosen.map((b) => b.applicationId),
        status,
        ...(reason.trim() && { reason: reason.trim() }),
      });
      toast.success(`${chosen.length === 1 ? fullName(chosen[0].applicant) : `${chosen.length} awardees`}: ${beneficiaryStatusOf(status).label}`);
      onDone(data);
    } catch (err) {
      console.error(`Failed to record ${status} for awardees of scholarship ${scholarship.id}:`, err);
      setError(errorMessageOf(err, "Failed to record the step. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissible={!busy}
      title={chosen.length === 1 ? `Update ${fullName(chosen[0].applicant)}` : `Update ${chosen.length} awardees`}
      description={chosen.length > 1 ? names(chosen) : `Now: ${beneficiaryStatusOf(chosen[0].status).label}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} loading={busy} disabled={!status || unchanged.length > 0}>
            Record step
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select label="New step" required placeholder="Choose a step" options={BENEFICIARY_STATUSES} value={status} onChange={(e) => setStatus(e.target.value)} />
        {unchanged.length > 0 && <p className="text-sm text-red-600">Already {beneficiaryStatusOf(status).label.toLowerCase()}: {names(unchanged)}. Leave them out of the selection.</p>}
        <Textarea label="Note (optional)" rows={3} maxLength={MAX_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

/**
 * The scholarship's awardees whose award stands, best HEC rank first. With `canChange` (HEC: the
 * sponsor only grants or refuses), one or many are selected and their next step recorded. `basePath` is how the viewer reads
 * a full application: "admin" for HEC, "staff" for the owner. `focusId` opens that awardee's
 * progress dialog once the list is loaded, then `onFocused` is called.
 */
export default function BeneficiariesPanel({ scholarship, canChange, basePath, focusId, onFocused }) {
  const queryClient = useQueryClient();
  const { data: awardees = [], isPending, isError, refetch } = useBeneficiaries(scholarship.id);
  const [selected, setSelected] = useState([]); // application IDs
  const [updating, setUpdating] = useState(null); // awardees waiting in the status dialog
  const [openId, setOpenId] = useState(null);

  // Sent here from a nominee's "Record progress"
  useEffect(() => {
    if (!focusId || isPending) return;
    const awardee = awardees.find((b) => b.applicationId === focusId);
    if (awardee && canChange) setUpdating([awardee]);
    else console.warn(`Application ${focusId} is not among the awardees of scholarship ${scholarship.id}`);
    onFocused?.();
  }, [focusId, isPending, awardees, canChange, onFocused, scholarship.id]);

  if (isError) return <EmptyState title="Could not load the awardees" onRefresh={refetch} />;

  const allSelected = awardees.length > 0 && awardees.every((b) => selected.includes(b.applicationId));
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const counts = awardees.reduce((acc, b) => ({ ...acc, [b.status ?? "NONE"]: (acc[b.status ?? "NONE"] ?? 0) + 1 }), {});

  const done = (fresh) => {
    queryClient.setQueryData(beneficiariesKey(scholarship.id), fresh);
    queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
    setSelected([]);
    setUpdating(null);
  };

  const columns = [
    ...(canChange
      ? [
          {
            key: "select",
            label: (
              <input
                type="checkbox"
                aria-label="Select every awardee"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? [] : awardees.map((b) => b.applicationId))}
                className="h-4 w-4 accent-brand"
              />
            ),
            exportable: false,
            sortable: false,
            render: (b) => (
              <input
                type="checkbox"
                aria-label={`Select ${fullName(b.applicant)}`}
                checked={selected.includes(b.applicationId)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(b.applicationId)}
                className="h-4 w-4 accent-brand"
              />
            ),
          },
        ]
      : []),
    { key: "rank", label: "HEC rank", accessor: (b) => b.rank ?? "—", sortValue: (b) => b.rank ?? Infinity },
    { key: "applicant", label: "Awardee", accessor: (b) => fullName(b.applicant), render: (b) => <span className="font-medium text-slate-800">{fullName(b.applicant)}</span> },
    { key: "email", label: "Email", accessor: (b) => b.applicant.email },
    { key: "phone", label: "Phone", accessor: (b) => b.applicant.phoneNumber ?? "—" },
    { key: "programme", label: "Programme", accessor: (b) => b.programme.name, filter: "Any programme" },
    { key: "meanScore", label: "HEC score", accessor: (b) => formatScore(b.meanScore), sortValue: (b) => b.meanScore },
    {
      key: "status",
      label: "Follow-up",
      accessor: (b) => beneficiaryStatusOf(b.status).label,
      filter: "Any step",
      render: (b) => (
        <div className="flex flex-col gap-1">
          {statusTag(b)}
          {b.reason && <span className="max-w-xs text-xs text-slate-500">{b.reason}</span>}
        </div>
      ),
    },
    { key: "updatedAt", label: "Since", accessor: (b) => formatDateTime(b.updatedAt ?? b.awardedAt), sortValue: (b) => b.updatedAt ?? b.awardedAt },
    {
      key: "actions",
      label: "Actions",
      exportable: false,
      render: (b) => (
        <RowMenu
          actions={[
            { Icon: EyeIcon, label: "View", onClick: () => setOpenId(b.applicationId) },
            canChange && { Icon: ArrowsRightLeftIcon, label: "Record progress", onClick: () => setUpdating([b]) },
          ]}
        />
      ),
    },
  ];

  const chosen = awardees.filter((b) => selected.includes(b.applicationId));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Tag color="blue">Granted: {counts.NONE ?? 0}</Tag>
        {BENEFICIARY_STATUSES.map((s) => (
          <Tag key={s.value} color={s.color}>
            {s.label}: {counts[s.value] ?? 0}
          </Tag>
        ))}
      </div>
      <Table
        columns={columns}
        data={awardees}
        onRowClick={(b) => setOpenId(b.applicationId)}
        isLoading={isPending}
        exportName={`awardees-${scholarship.name}`}
        emptyTitle="No approved awardees yet"
        actions={
          canChange && (
            <Button icon={<ArrowsRightLeftIcon className="h-4 w-4" />} onClick={() => setUpdating(chosen)} disabled={!chosen.length}>
              Record progress{chosen.length ? ` (${chosen.length})` : ""}
            </Button>
          )
        }
      />
      {updating && <StatusDialog scholarship={scholarship} chosen={updating} onClose={() => setUpdating(null)} onDone={done} />}
      <ApplicationDialog key={openId} applicationId={openId} programmes={scholarship.programmes} basePath={basePath} onClose={() => setOpenId(null)} />
    </div>
  );
}
